// src/services/notificationSoundService.js

/**
 * Simple notification sound service using HTML5 Audio
 * Plays a chime sound for alerts when sound is enabled
 */

let audioContext = null
let isUnlocked = false

// Base64-encoded simple notification chime (short, pleasant tone)
// This is a simple synthesized chime to avoid external file dependencies
const CHIME_DATA_URI =
  'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6ch3dpaXOCkJydl4x8bmZod4WSmJiThXhwbHJ/jJSXlI+CdnBweISPk5KPhnpzcnZ9hoyQj4uCeHR0d36GioqIg3x3dnd8goaHhYF9eXh5fIGEhYSBfnp5en2AgoOCgH17enp9f4GBgIB+fHt7fX+AgYCAfnx7e31/gICAgH58fHx9f4CAgIB+fHx8fX+AgIB/fnx8fH1/gICAf358fHx9f4CAgH9+fHx8fX9/f39/fnx8fH1/f39/f358fHx9f39/f35+fHx8fX9/f39+fnx8fH19f39/fn58fHx9fX9/f35+fHx8fX19f39+fnx8fH19fX9/fn58fHx9fX1/f35+fHx8fX19f39+fnx8fH19fX5/fn58fHx9fX1+fn5+fHx8fX19fn5+fnx8fH19fX5+fn58fHx9fX1+fn5+fHx8fX19fn5+fnx8fH19fX5+fn58fHx9fX1+fn5+fHx8fX19fn5+fnx8fH19fX5+fn58fHx9fX19fn5+fHx8fX19fX5+fnx8fH19fX1+fn58fH19fX19fn5+fHx9fX19fX5+fnx8fX19fX1+fn58fH19fX19fn5+fHx9fX19fX5+fn58fX19fX19fn5+fn19fX19fX5+fn59fX19fX19fn5+fn19fX19fX1+fn5+fX19fX19fX5+fn59fX19fX19fn5+fn19fX19fX1+fn5+fX19fX19fX5+fn59fX19fX19fn5+fn19fX19fX1+fn5+fX19fX19fX5+fn59fX19fX19fX5+fn19fX19fX19fn5+fX19fX19fX1+fn59fX19fX19fX5+fn19fX19fX19fn5+fX19fX19fX1+fn59fX19fX19fX5+fn19fX19fX19fn5+fX19fX19fX1+fn59fX19fX19fX5+fn19fX19fX19fX5+fX19fX19fX19fn59fX19fX19fX1+fn19fX19fX19fX5+fX19fX19fX19fn59fX19fX19fX1+fn19fX19fX19fX5+fX19fX19fX19fn59fX19fX19fX1+fn19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fX19fQ=='

/**
 * Create audio context on first user interaction
 * Required for browsers that block autoplay
 */
export function unlockAudio() {
  if (isUnlocked) return

  try {
    // Create a silent audio context to unlock audio
    audioContext = new (window.AudioContext || window.webkitAudioContext)()

    // Play a silent buffer to unlock
    const buffer = audioContext.createBuffer(1, 1, 22050)
    const source = audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(audioContext.destination)
    source.start(0)

    isUnlocked = true
    console.log('[notificationSoundService] Audio unlocked')
  } catch (e) {
    console.warn('[notificationSoundService] Failed to unlock audio:', e)
  }
}

/**
 * Play the notification chime sound
 * @returns {Promise<boolean>} Whether the sound played successfully
 */
export async function playNotificationSound() {
  try {
    const audio = new Audio(CHIME_DATA_URI)
    audio.volume = 0.5

    await audio.play()
    return true
  } catch (e) {
    console.warn('[notificationSoundService] Failed to play sound:', e)
    return false
  }
}

/**
 * Play a custom sound from a URL
 * @param {string} url - URL of the audio file
 * @param {number} volume - Volume level (0-1)
 * @returns {Promise<boolean>}
 */
export async function playSound(url, volume = 0.5) {
  try {
    const audio = new Audio(url)
    audio.volume = Math.max(0, Math.min(1, volume))

    await audio.play()
    return true
  } catch (e) {
    console.warn('[notificationSoundService] Failed to play sound:', e)
    return false
  }
}

/**
 * Generate a simple notification tone using Web Audio API
 * More reliable than base64 audio in some browsers
 */
export function playGeneratedTone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()

    // Create oscillator for the tone
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Pleasant notification frequency (around C6)
    oscillator.frequency.value = 1047
    oscillator.type = 'sine'

    // Envelope: quick attack, medium decay
    const now = ctx.currentTime
    gainNode.gain.setValueAtTime(0, now)
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01)
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3)

    oscillator.start(now)
    oscillator.stop(now + 0.3)

    return true
  } catch (e) {
    console.warn('[notificationSoundService] Failed to generate tone:', e)
    return false
  }
}

export default {
  unlockAudio,
  playNotificationSound,
  playSound,
  playGeneratedTone,
}
